import { useNavigate } from 'react-router-dom';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { usePermisos } from '@/hooks/usePermisos';
import { useAuth } from '@/context/AuthContextApi';
import { FaSignOutAlt } from 'react-icons/fa';

/**
 * Página mostrada cuando el usuario no tiene permisos suficientes
 */
export const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const { empresaActual, usuarioEmpresa, isSuperAdmin } = useSimpleEmpresa();
  const { isAdmin, isMecanico, isCliente } = usePermisos();
  const { logout } = useAuth();

  const handleGoHome = () => {
    // Redirigir según el rol del usuario
    if (isSuperAdmin || isAdmin()) {
      navigate('/dashboard');
    } else if (isMecanico()) {
      navigate('/mantenimientos');
    } else if (isCliente()) {
      navigate('/mis-vehiculos');
    } else {
      navigate('/');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Incluso si hay error, redirigir al login
      navigate('/login');
    }
  };

  const getRoleName = () => {
    if (isSuperAdmin) return 'Super Administrador';
    if (!usuarioEmpresa) return 'Sin rol asignado';
    
    switch (usuarioEmpresa.perfil_id) {
      case 'admin': return 'Administrador';
      case 'mecanico': return 'Mecánico';
      case 'cliente': return 'Cliente';
      default: return usuarioEmpresa.perfil_id;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        {/* Icono de acceso denegado */}
        <div className="text-red-400 text-8xl mb-6">
          🚫
        </div>

        {/* Título */}
        <h1 className="text-3xl font-bold text-white mb-4">
          Acceso Denegado
        </h1>

        {/* Mensaje principal */}
        <p className="text-gray-300 text-lg mb-6">
          No tienes permisos suficientes para acceder a esta página.
        </p>

        {/* Información del usuario */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6 text-left">
          <h3 className="text-white font-semibold mb-3">Información de tu cuenta:</h3>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Empresa:</span>
              <span className="text-white">
                {empresaActual?.nombre || 'No seleccionada'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-400">Rol:</span>
              <span className="text-white">
                {getRoleName()}
              </span>
            </div>
            
            {empresaActual && (
              <div className="flex justify-between">
                <span className="text-gray-400">Plan:</span>
                <span className="text-white capitalize">
                  {empresaActual.plan_id?.replace('revvly_', '') || 'No definido'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Sugerencias */}
        <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mb-6">
          <h4 className="text-blue-400 font-semibold mb-2">¿Qué puedes hacer?</h4>
          <ul className="text-sm text-gray-300 text-left space-y-1">
            <li>• Contacta al administrador de tu empresa para solicitar permisos</li>
            <li>• Verifica que estás en la empresa correcta</li>
            <li>• Regresa a la página principal de tu perfil</li>
          </ul>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleGoHome}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Ir a Página Principal
          </button>
          
          <button
            onClick={() => navigate(-1)}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Volver Atrás
          </button>
        </div>

        {/* Botón de cerrar sesión */}
        <div className="mt-4">
          <button
            onClick={handleLogout}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <FaSignOutAlt />
            Cerrar Sesión
          </button>
        </div>

        {/* Información de contacto */}
        <div className="mt-8 text-sm text-gray-400">
          <p>
            ¿Necesitas ayuda? Contacta a soporte en{' '}
            <a 
              href="mailto:soporte@revvly.com" 
              className="text-blue-400 hover:text-blue-300"
            >
              soporte@revvly.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage;